/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";

import { createApplicationRecord, createScreeningAnswersComposite } from "../../Auth/saaedaApi";
import { uploadCvToServer } from "./services/uploadCv";
import { queryScreeningQuestions } from "./services/salesforceScreeningQuestions";

// ====== CONFIGURE THESE ======
const CV_FIELD_API = "cvURL__c"; // Salesforce field that stores the uploaded CV URL

// ====== Helpers ======
function stripQuotes(s) {
  return String(s).trim().replace(/^["']|["']$/g, "");
}

function parseOptions(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw.map((v) => stripQuotes(v));
  const s = String(raw).trim();

  // Try JSON array first: ["A","B","C"]
  if (s.startsWith("[") || s.startsWith("{")) {
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) return parsed.map((v) => stripQuotes(v));
    } catch {
      // fall through
    }
  }

  // Fallback separators: newline / semicolon / comma
  return s
    .split(/\r?\n|;|,/)
    .map((x) => stripQuotes(x))
    .map((x) => x.trim())
    .filter(Boolean);
}

function normalizeType(type) {
  if (!type) return "Open text (single line)";
  const t = String(type).toLowerCase().trim();
  if (t.includes("single line")) return "Open text (single line)";
  if (t.includes("multi line")) return "Open text (multi line)";
  if (t.includes("decimal")) return "Decimal number";
  if (t.includes("multiple choice") || t.includes("multi select") || t.includes("checkbox") || t.includes("multi-select")) return "Multiple choice";
  if (t.includes("radio") || t.includes("single choice")) return "Single choice (radio)";
  if (t.includes("dropdown") || t.includes("picklist") || t.includes("select")) return "Single choice (dropdown)";
  if (t.includes("yes") || t.includes("no")) return "Yes/no";
  if (t === "url" || t === "link") return "URL";
  if (t === "address") return "Address";
  if (t === "date") return "Date";
  return type;
}

function isEmptyAnswer(v) {
  return (
    v === undefined ||
    v === null ||
    (typeof v === "string" && v.trim() === "") ||
    (Array.isArray(v) && v.length === 0) ||
    (typeof v === "object" && !Array.isArray(v) && v.url === undefined)
  );
}

function stringifyAnswer(type, v) {
  if (v == null) return "";
  if (Array.isArray(v)) return v.join("; ");
  if (typeof v === "object" && v.url) return v.url; // file value
  return String(v); // number/date/url/string/bool
}

function truncate255(s) {
  const str = String(s ?? "");
  return str.length > 255 ? str.slice(0, 255) : str;
}

// ---------- Create child answers (via backend composite proxy) ----------
async function createScreeningAnswers(appId, qList, ansMap) {
  const records = qList
    .map((q) => {
      const type = normalizeType(q.Type__c);
      const raw = ansMap[q.Id];
      const value = stringifyAnswer(type, raw);
      if (isEmptyAnswer(raw)) return null; // only persist answered questions
      return {
        attributes: { type: "Screen_Questions_Answer__c" },
        First_Application__c: appId,
        Question__c: truncate255(q.Title__c || ""),
        Answer__c: truncate255(value),
      };
    })
    .filter(Boolean);

  if (!records.length) return;
  await createScreeningAnswersComposite(records);
}

// ---------- Inline Question Renderer ----------
function QuestionRenderer({ q, value, onChange, disabled, error, onFileUpload }) {
  const type = normalizeType(q.Type__c);
  const opts = parseOptions(q.Options__c);
  const reqMark = q.Required__c ? <span className="text-danger ms-1">*</span> : null;
  const commonErr = error ? <div className="invalid-feedback d-block">{error}</div> : null;

  switch (type) {
    case "Open text (single line)":
      return (
        <div className="mb-3" data-qid={q.Id}>
          <label className="form-label fw-semibold">
            {q.Title__c}
            {reqMark}
          </label>
          <input
            className={`form-control ${error ? "is-invalid" : ""}`}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
          />
          {commonErr}
        </div>
      );

    case "Open text (multi line)":
      return (
        <div className="mb-3" data-qid={q.Id}>
          <label className="form-label fw-semibold">
            {q.Title__c}
            {reqMark}
          </label>
          <textarea
            className={`form-control ${error ? "is-invalid" : ""}`}
            rows={4}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
          />
          {commonErr}
        </div>
      );

    case "Decimal number":
      return (
        <div className="mb-3" data-qid={q.Id}>
          <label className="form-label fw-semibold">
            {q.Title__c}
            {reqMark}
          </label>
          <input
            type="number"
            step="0.01"
            className={`form-control ${error ? "is-invalid" : ""}`}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
            disabled={disabled}
          />
          {commonErr}
        </div>
      );

    case "Multiple choice":
      return (
        <div className="mb-3" data-qid={q.Id}>
          <div className="form-label fw-semibold">
            {q.Title__c}
            {reqMark}
          </div>
          <div className="d-flex flex-wrap gap-3">
            {opts.map((o, idx) => {
              const checked = Array.isArray(value) ? value.includes(o) : false;
              const optId = `${q.Id}_opt_${idx}`;
              return (
                <div className="form-check" key={optId}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={optId}
                    checked={checked}
                    disabled={disabled}
                    onChange={(e) => {
                      if (e.target.checked) onChange([...(value || []), o]);
                      else onChange((value || []).filter((v) => v !== o));
                    }}
                  />
                  <label className="form-check-label" htmlFor={optId}>
                    {o}
                  </label>
                </div>
              );
            })}
          </div>
          {commonErr}
        </div>
      );

    case "Single choice (radio)":
      return (
        <div className="mb-3" data-qid={q.Id}>
          <div className="form-label fw-semibold">
            {q.Title__c}
            {reqMark}
          </div>
          <div className="d-flex flex-column gap-2">
            {opts.map((o, idx) => {
              const optId = `${q.Id}_radio_${idx}`;
              return (
                <div className="form-check" key={optId}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name={`radio_${q.Id}`}
                    id={optId}
                    checked={value === o}
                    disabled={disabled}
                    onChange={() => onChange(o)}
                  />
                  <label className="form-check-label" htmlFor={optId}>
                    {o}
                  </label>
                </div>
              );
            })}
          </div>
          {commonErr}
        </div>
      );

    case "Single choice (dropdown)":
      return (
        <div className="mb-3" data-qid={q.Id}>
          <label className="form-label fw-semibold">
            {q.Title__c}
            {reqMark}
          </label>
          <select
            className={`form-select ${error ? "is-invalid" : ""}`}
            value={value || ""}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="" disabled>
              Choose…
            </option>
            {opts.map((o, idx) => (
              <option key={`${q.Id}_dd_${idx}`} value={o}>
                {o}
              </option>
            ))}
          </select>
          {commonErr}
        </div>
      );

    case "Yes/no":
      return (
        <div className="mb-3" data-qid={q.Id}>
          <div className="form-label fw-semibold">
            {q.Title__c}
            {reqMark}
          </div>
          <div className="d-flex gap-2">
            <button
              type="button"
              className={`btn ${value === true ? "btn-success" : "btn-outline-success"}`}
              disabled={disabled}
              onClick={() => onChange(true)}
            >
              Yes
            </button>
            <button
              type="button"
              className={`btn ${value === false ? "btn-danger" : "btn-outline-danger"}`}
              disabled={disabled}
              onClick={() => onChange(false)}
            >
              No
            </button>
          </div>
          {commonErr}
        </div>
      );

    case "URL":
      return (
        <div className="mb-3" data-qid={q.Id}>
          <label className="form-label fw-semibold">
            {q.Title__c}
            {reqMark}
          </label>
          <input
            type="url"
            className={`form-control ${error ? "is-invalid" : ""}`}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            placeholder="https://example.com"
          />
          {commonErr}
        </div>
      );

    case "Address":
      return (
        <div className="mb-3" data-qid={q.Id}>
          <label className="form-label fw-semibold">
            {q.Title__c}
            {reqMark}
          </label>
          <textarea
            className={`form-control ${error ? "is-invalid" : ""}`}
            rows={3}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            placeholder="Street, City, State, Postal code, Country"
          />
          {commonErr}
        </div>
      );

    case "Date":
      return (
        <div className="mb-3" data-qid={q.Id}>
          <label className="form-label fw-semibold">
            {q.Title__c}
            {reqMark}
          </label>
          <input
            type="date"
            className={`form-control ${error ? "is-invalid" : ""}`}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
          />
          {commonErr}
        </div>
      );

    default:
      return (
        <div className="mb-3" data-qid={q.Id}>
          <label className="form-label fw-semibold">
            {q.Title__c}
            {reqMark}
          </label>
          <input
            className={`form-control ${error ? "is-invalid" : ""}`}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
          />
          {commonErr}
        </div>
      );
  }
}

// ---------- Main Form ----------
const EasyApplyForm = ({ vacancyId }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState({ type: "", message: "" });

  // Personal info
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [files, setFiles] = useState([]);

  
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [qErrors, setQErrors] = useState({});

  useEffect(() => {
    if (!vacancyId) return;

    (async () => {
      try {
        const qs = await queryScreeningQuestions(vacancyId);
        setQuestions(qs);
      } catch (e) {
        console.error("Failed to load screening questions:", e?.response?.data || e);
      }
    })();
  }, [vacancyId]);

  const handleChange = (e) => setForm((s) => ({ ...s, [e.target.name]: e.target.value }));
  const handleFileChange = (e) => setFiles(Array.from(e.target.files));

  const clearStatus = () => setTimeout(() => setSubmitStatus({ type: "", message: "" }), 5000);

  const resetForm = () => {
    setForm({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
    });
    setFiles([]);
    setErrors({});
    setAnswers({});
    setQErrors({});
  };

  // ----- Validation -----
  const validatePersonal = () => {
    const err = {};
    if (!form.firstName.trim()) err.firstName = "First name is required.";
    if (!form.lastName.trim()) err.lastName = "Last name is required.";
    if (!form.email.trim()) err.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) err.email = "Enter a valid email.";
    if (!form.phone.trim()) err.phone = "Phone is required.";
    if (!files.length) err.files = "Please attach your CV (PDF/DOC/DOCX/RTF).";
    return err;
  };

  const validateQuestions = () => {
    const err = {};
    for (const q of questions) {
      const type = normalizeType(q.Type__c);
      const v = answers[q.Id];

      // Only enforce Salesforce Required__c
      if (q.Required__c && isEmptyAnswer(v)) {
        err[q.Id] = "This question is required.";
        continue;
      }

      if (v != null && v !== "") {
        if (type === "Decimal number" && typeof v !== "number") {
          err[q.Id] = "Enter a valid number.";
        }
        if (type === "URL" && typeof v === "string") {
          try {
            new URL(v);
          } catch {
            err[q.Id] = "Enter a valid URL.";
          }
        }
        if (type === "Date" && typeof v === "string") {
          if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) err[q.Id] = "Pick a valid date.";
        }
      }
    }
    return err;
  };

  const uploadQuestionFile = async (file) => {
    const { url } = await uploadCvToServer(file, {
      endpoint: "https://cv-server-upload.saaeda.com/api/upload-cv",
    });
    return { url, name: file.name };
  };

  const invalid = (key) => (errors[key] ? "is-invalid" : "");
  const errMsg = (key) => (errors[key] ? <div className="invalid-feedback d-block">{errors[key]}</div> : null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setQErrors({});
    setSubmitStatus({ type: "", message: "" });
    setIsSubmitting(true);

    const pErr = validatePersonal();
    const sErr = validateQuestions();

    if (Object.keys(pErr).length || Object.keys(sErr).length) {
      setErrors(pErr);
      setQErrors(sErr);
      setIsSubmitting(false);
      const firstKey = Object.keys(pErr)[0] || Object.keys(sErr)[0];
      const el =
        document.querySelector(`[name="${firstKey}"]`) ||
        document.querySelector(`[data-qid="${firstKey}"]`);
      if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    if (!vacancyId) {
      setIsSubmitting(false);
      setSubmitStatus({ type: "error", message: "Missing vacancy. Please go back and select a position first." });
      clearStatus();
      return;
    }

    // 1) Upload CV first
    let cvUrl = "";
    try {
      const { url } = await uploadCvToServer(files[0], {
        endpoint: "https://cv-server-upload.saaeda.com/api/upload-cv",
      });
      cvUrl = url;
    } catch (uploadErr) {
      console.error("Upload to server failed:", uploadErr);
      setIsSubmitting(false);
      setSubmitStatus({ type: "error", message: `Upload failed: ${uploadErr.message}` });
      clearStatus();
      return;
    }

    // 2) Build SF payload (application)
    const payload = {
      Vacancies__c: vacancyId,
      First_Name__c: form.firstName,
      Last_Name__c: form.lastName,
      Email__c: form.email,
      Phone__c: form.phone,
      Status__c: "Submitted",
      [CV_FIELD_API]: cvUrl,
    };

    // 3) Create the application then child answers (via backend proxy)
    try {
      const appId = await createApplicationRecord(payload);
      await createScreeningAnswers(appId, questions, answers);

      setSubmitStatus({
        type: "success",
        message: "Your application has been submitted successfully! We will contact you soon.",
      });
      resetForm();
    } catch (err) {
      console.error("Create failed:", err);
      setSubmitStatus({ type: "error", message: "Could not submit application. Please try again." });
    } finally {
      setIsSubmitting(false);
      clearStatus();
    }
  };

  return (
    <div>
      <h4 className="mb-3" style={{ color: "#008080" }}>
        Easy Apply
      </h4>

      {submitStatus.type === "success" && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          <div className="d-flex align-items-center">
            <i className="fas fa-check-circle me-2 fs-4"></i>
            <div>
              <strong>Success!</strong> {submitStatus.message}
            </div>
          </div>
          <button type="button" className="btn-close" onClick={() => setSubmitStatus({ type: "", message: "" })} />
        </div>
      )}

      {submitStatus.type === "error" && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <div className="d-flex align-items-center">
            <i className="fas fa-exclamation-circle me-2 fs-4"></i>
            <div>
              <strong>Error!</strong> {submitStatus.message}
            </div>
          </div>
          <button type="button" className="btn-close" onClick={() => setSubmitStatus({ type: "", message: "" })} />
        </div>
      )}

      {Object.keys(errors).length > 0 && (
        <div className="alert alert-danger">Please fix the highlighted fields and try again.</div>
      )}

      {submitStatus.type !== "success" ? (
        <form onSubmit={handleSubmit} aria-busy={isSubmitting}>
          {/* Personal information */}
          <h6 className="fw-semibold mb-3">Personal information</h6>
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <input
                className={`form-control ${invalid("firstName")}`}
                name="firstName"
                placeholder="First name"
                value={form.firstName}
                onChange={handleChange}
                disabled={isSubmitting}
              />
              {errMsg("firstName")}
            </div>
            <div className="col-md-6">
              <input
                className={`form-control ${invalid("lastName")}`}
                name="lastName"
                placeholder="Last name"
                value={form.lastName}
                onChange={handleChange}
                disabled={isSubmitting}
              />
              {errMsg("lastName")}
            </div>
            <div className="col-md-6">
              <input
                className={`form-control ${invalid("email")}`}
                name="email"
                placeholder="Your email"
                value={form.email}
                onChange={handleChange}
                disabled={isSubmitting}
              />
              {errMsg("email")}
            </div>
            <div className="col-md-6">
              <input
                className={`form-control ${invalid("phone")}`}
                name="phone"
                placeholder="Phone number"
                value={form.phone}
                onChange={handleChange}
                disabled={isSubmitting}
              />
              {errMsg("phone")}
            </div>
          </div>

          {/* Files */}
          <div className="mb-4">
            <label className="form-label fw-semibold">CV / Resume (pdf, doc, docx, rtf)</label>
            <div className={`border border-2 border-dashed rounded p-3 bg-light ${invalid("files")}`}>
              <input
                type="file"
                accept=".pdf,.doc,.docx,.rtf,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleFileChange}
                className="form-control"
                disabled={isSubmitting}
              />
              <div className="small text-muted mt-2">
                {files.length ? files.map((f) => f.name).join(", ") : "Drop your file here or choose file"}
              </div>
            </div>
            {errMsg("files")}
          </div>


      
          
          {questions.length > 0 && (
            <section className="card border-0 shadow-sm my-4">
              <div className="card-body">
               
                {questions.map((q) => (
                  <QuestionRenderer
                    key={q.Id}
                    q={q}
                    value={answers[q.Id]}
                    error={qErrors[q.Id]}
                    disabled={isSubmitting}
                    onChange={(v) => {
                      setAnswers((s) => ({ ...s, [q.Id]: v }));
                      setQErrors((prev) => {
                        if (!prev[q.Id]) return prev;
                        const next = { ...prev };
                        delete next[q.Id];
                        return next;
                      });
                    }}
                    onFileUpload={uploadQuestionFile}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Submit Button */}
          <div className="text-center">
            <button type="submit" className="btn btn-primary px-4" disabled={isSubmitting}>
              {isSubmitting && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />}
              {isSubmitting ? "Submitting..." : "Submit application"}
            </button>
          </div>
        </form>
      ) : (
        <div className="text-center py-5">
          <div className="alert alert-success d-inline-block">
            <div className="d-flex align-items-center">
              <i className="fas fa-check-circle me-3 fs-1"></i>
              <div className="text-start">
                <h5 className="mb-1">Application Submitted Successfully!</h5>
                <p className="mb-0">{submitStatus.message}</p>
              </div>
            </div>
          </div>
          <div className="mt-4">
            <button className="btn btn-outline-primary me-2" onClick={() => setSubmitStatus({ type: "", message: "" })}>
              Submit Another Application
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EasyApplyForm;
